import React, { useState, useEffect } from 'react';
import CustomDropdown from './CustomDropdown';
import { useTranslation } from 'react-i18next';
import './EmergencyProtocolModal.css';
import { API_URL } from './config/apiConfig';

// Faction colors for orbs (matching Strategy.js)
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

// Get faction orb background color/gradient
const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};

function EmergencyProtocolModal({ isOpen, onClose, onSave, initialData }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const [factions, setFactions] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [selectedFactionId, setSelectedFactionId] = useState(null);
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      // Reset to initial data when modal opens
      const factionId = initialData?.faction_id || null;
      const strategyId = initialData?.strategy_id || null;
      setSelectedFactionId(factionId);
      setSelectedStrategyId(strategyId);
    }
  }, [isOpen, initialData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const currentLanguage = i18nInstance.language || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');

      // Fetch factions
      const factionsRes = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Accept-Language': normalizedLanguage,
        },
      });

      if (factionsRes.ok) {
        const factionsData = await factionsRes.json();
        setFactions(factionsData || []);
      }

      // Fetch strategies
      const strategiesRes = await fetch(`${API_URL}/my-strategies`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (strategiesRes.ok) {
        const strategiesData = await strategiesRes.json();
        if (Array.isArray(strategiesData)) {
          setStrategies(strategiesData);
        }
      }
    } catch (err) {
      console.error('Error fetching data for emergency protocol modal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    if (selectedFactionId && selectedStrategyId) {
      onSave({
        faction_id: parseInt(selectedFactionId),
        strategy_id: parseInt(selectedStrategyId)
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  // Get faction translations
  const factionTranslations = {};
  factions.forEach(f => {
    if (f.faction_name_translated) {
      factionTranslations[f.faction_name] = f.faction_name_translated;
    }
  });

  const factionOptions = factions.map(f => ({
    value: f.id.toString(),
    label: f.faction_name_translated || f.faction_name,
    faction_name: f.faction_name
  }));

  const strategyOptions = strategies.map(s => ({
    value: s.id.toString(),
    label: s.name,
    faction_name: s.faction_name
  }));

  return (
    <div className="emergency-protocol-modal-overlay" onClick={onClose}>
      <div className="emergency-protocol-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="emergency-protocol-modal-header">
          <h2>{t('profile.emergencyProtocol.title', 'Reactive Doctrine Configuration')}</h2>
          <button className="emergency-protocol-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="emergency-protocol-modal-body">
          {loading ? (
            <div className="emergency-protocol-loading">Loading...</div>
          ) : (
            <>
              <div className="emergency-protocol-field">
                <label>{t('profile.emergencyProtocol.selectFaction', 'Select a faction that should trigger the strategy switching')}</label>
                <CustomDropdown
                  options={factionOptions}
                  value={selectedFactionId ? selectedFactionId.toString() : ''}
                  onChange={(value) => setSelectedFactionId(value ? parseInt(value) : null)}
                  placeholder={t('profile.emergencyProtocol.selectFactionPlaceholder', 'Select Faction')}
                  renderOption={(option) => {
                    const faction = factions.find(f => f.id.toString() === option.value);
                    const factionName = faction?.faction_name || option.faction_name;
                    const translatedName = factionTranslations[factionName] || factionName || option.label;
                    
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          className="faction-orb"
                          style={{ background: getFactionOrbBackground(factionName) }}
                          title={translatedName}
                        />
                        <span>{translatedName}</span>
                      </div>
                    );
                  }}
                />
              </div>

              <div className="emergency-protocol-field">
                <label>{t('profile.emergencyProtocol.selectStrategy', 'Select the alternative strategy, you want to switch to')}</label>
                <CustomDropdown
                  options={strategyOptions}
                  value={selectedStrategyId ? selectedStrategyId.toString() : ''}
                  onChange={(value) => setSelectedStrategyId(value ? parseInt(value) : null)}
                  placeholder={t('profile.emergencyProtocol.selectStrategyPlaceholder', 'Select Strategy')}
                  renderOption={(option) => {
                    const strategy = strategies.find(s => s.id.toString() === option.value);
                    const factionName = strategy?.faction_name || option.faction_name;
                    if (!factionName) {
                      return <span>{option.label}</span>;
                    }
                    const translatedFactionName = factionTranslations[factionName] || factionName;
                    
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          className="faction-orb"
                          style={{ background: getFactionOrbBackground(factionName) }}
                          title={translatedFactionName}
                        />
                        <span>{option.label}</span>
                      </div>
                    );
                  }}
                />
              </div>
            </>
          )}
        </div>

        <div className="emergency-protocol-modal-footer">
          <button 
            className="emergency-protocol-done-button"
            onClick={handleDone}
            disabled={!selectedFactionId || !selectedStrategyId || loading}
          >
            {t('common.done', 'Done')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmergencyProtocolModal;

